<template>
  <div>
    <div v-if="errorMessage" class="alert alert-danger py-2">{{ errorMessage }}</div>

    <div class="row g-3">

  <!-- Vente -->
  <div class="col-md-7">
    <label class="form-label small fw-semibold">Vente *</label>

    <select
      v-model.number="form.sale_id"
      class="form-select"
      :disabled="mode === 'edit'"
    >
      <option :value="null">Sélectionnez une vente validée</option>
      <option
        v-for="option in salesOptions"
        :key="option.value"
        :value="option.value"
      >
        {{ option.label }}
      </option>
    </select>

    <small class="text-muted">
      Une vente validée = 1 livraison (par défaut).
    </small>
  </div>

  <!-- Frais -->
  <div class="col-md-5">
    <label class="form-label small fw-semibold">Frais de livraison</label>

    <input
      type="number"
      class="form-control"
      v-model.number="form.delivery_fee"
      min="0"
      step="0.01"
      disabled
    />

    <small class="text-muted">
      Le montant est calculé automatiquement
    </small>
  </div>

  <!-- Zone -->
  <div class="col-md-7">
    <label class="form-label small fw-semibold">Zone *</label>

    <select
      v-model="form.zone_choice"
      class="form-select"
      @change="onZoneChanged"
    >
      <option :value="null">Sélectionnez une zone</option>
      <option
        v-for="option in zoneChoiceOptions"
        :key="option.value"
        :value="option.value"
      >
        {{ option.label }}
      </option>
    </select>

    <!-- Autre -->
    <div v-if="form.zone_choice === 'other'" class="mt-2">
      <label class="form-label small fw-semibold">
        Nom de la zone (Autre) *
      </label>
      <input
        type="text"
        v-model="form.zone_name_custom"
        class="form-control"
        placeholder="Ex: Alibandeng (autre)"
      />
    </div>
  </div>

  <!-- Livreur -->
  <div class="col-md-5">
    <div class="d-flex justify-content-between align-items-center">
      <label class="form-label small fw-semibold mb-1">Livreur</label>

      <!-- toggle -->
      <div class="btn-group btn-group-sm">
        <button
          type="button"
          class="btn"
          :class="delivererMode === 'existing'
            ? 'btn-primary'
            : 'btn-outline-primary'"
          @click="delivererMode = 'existing'"
        >
          Existant
        </button>

        <button
          type="button"
          class="btn"
          :class="delivererMode === 'new'
            ? 'btn-primary'
            : 'btn-outline-primary'"
          @click="delivererMode = 'new'"
        >
          Nouveau
        </button>
      </div>
    </div>

    <!-- Livreur existant -->
    <div v-if="delivererMode === 'existing'">
      <select
        v-model.number="form.deliverer_id"
        class="form-select"
      >
        <option :value="null">Sélectionnez un livreur</option>
        <option
          v-for="option in deliverersOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </div>

    <!-- Nouveau livreur -->
    <div v-else class="mt-1">
      <input
        type="text"
        v-model="form.deliverer_name"
        class="form-control mb-2"
        placeholder="Nom du livreur"
      />

      <input
        type="tel"
        v-model="form.deliverer_phone"
        class="form-control"
        placeholder="Téléphone (optionnel)"
        @input="sanitizePhoneField"
      />
    </div>
  </div>

  <!-- Notes -->
  <div class="col-md-12">
    <label class="form-label small fw-semibold">Notes</label>
    <input
      type="text"
      v-model="form.notes"
      class="form-control"
      placeholder="Instructions de livraison (optionnel)"
    />
  </div>

</div>


    <div class="d-flex justify-content-end gap-2 mt-4">
      <button class="btn btn-outline-secondary" type="button" :disabled="isSubmitting" @click="$emit('cancel')">
        <i class="fa fa-xmark me-1" aria-hidden="true"></i>
        Annuler
      </button>
      <AppLoadingButton
        class="btn btn-primary"
        type="button"
        :loading="isSubmitting"
        loading-label="Enregistrement..."
        @click="submit"
      >
        <i class="fa fa-save me-1" aria-hidden="true"></i>
        Enregistrer
      </AppLoadingButton>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { isPhoneInputValid, phoneInputValidationMessage, sanitizePhoneInput } from '@/utils/phone';

export default {
  name: 'DeliveryForm',
  props: {
    mode: { type: String, required: true }, // create|edit
    delivery: { type: Object, default: null },
    zones: { type: Array, default: () => [] },
    deliverers: { type: Array, default: () => [] },
  },
  emits: ['saved', 'cancel'],
  data() {
    return {
      isSubmitting: false,
      errorMessage: '',

      defaultFee: 0,
      sales: [],

      delivererMode: 'existing', // existing|new

      form: {
        sale_id: null,

        // zone_choice: zoneId OR 'other'
        zone_choice: null,
        delivery_zone_id: null,
        zone_name_custom: '',

        deliverer_id: null,
        deliverer_name: '',
        deliverer_phone: '',

        delivery_fee: 0,
        notes: '',
      },
    };
  },
  computed: {
    zoneChoiceOptions() {
      const base = (this.zones || []).map(z => ({ label: `${z.name} (${this.$format.currency(z.fee || 0)})`, value: z.id }));
      base.push({ label: `Autre (tarif par défaut)`, value: 'other' });
      return base;
    },
    deliverersOptions() {
      return [{ label: 'Aucun', value: null }].concat((this.deliverers || []).map(d => ({ label: d.name, value: d.id })));
    },
    salesOptions() {
      return (this.sales || []).map(s => ({
        value: s.id,
        label: `${s.reference} — ${(s.customer_name || 'comptant')} — ${this.$format.currency(s.total_amount || 0)}`,
      }));
    },
  },
  mounted() {
    this.bootstrap();
  },
  methods: {
    async bootstrap() {
      await this.fetchDefaultFee();
      await this.fetchEligibleSales();
      this.initFromProps();
    },

    async fetchDefaultFee() {
      try {
        const res = await this.$api.get('/shop-info');
        const settings = res.data.data || res.data || null;
        this.defaultFee = Number(settings?.delivery_fee_default || 0);
      } catch (e) {
        console.error('fetchDefaultFee', e);
        this.defaultFee = 0;
      }
    },


    async fetchEligibleSales() {
      try {
        // simple: récupère les ventes et filtre côté front
        const res = await this.$api.get('/sales', { params: { status: 'validated' } });
        const sales = res.data.data || res.data || [];

        // Si ton API retourne déjà delivery_id -> top.
        // Sinon on filtre par un appel deliveries et exclusion.
        let deliveriesIds = [];
        try {
          const delRes = await this.$api.get('/deliveries');
          const dels = delRes.data.data || [];
          deliveriesIds = dels.map(d => d.sale_id).filter(Boolean);
        } catch (e) { }

        this.sales = (sales || [])
          .filter(s => (s.status === 'validated'))
          .filter(s => !deliveriesIds.includes(s.id))
          .sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
      } catch (e) {
        console.error('fetchEligibleSales', e);
        this.sales = [];
      }
    },

    initFromProps() {
      if (this.mode === 'edit' && this.delivery) {
        const d = this.delivery;

        this.form.sale_id = d.sale_id || d.sale?.id || null;
        this.form.delivery_fee = Number(d.delivery_fee || 0);
        this.form.notes = d.notes || '';

        if (d.delivery_zone_id) {
          this.form.zone_choice = d.delivery_zone_id;
          this.form.delivery_zone_id = d.delivery_zone_id;
          this.form.zone_name_custom = '';
        } else {
          this.form.zone_choice = 'other';
          this.form.delivery_zone_id = null;
          this.form.zone_name_custom = d.zone_name_snapshot || '';
        }

        if (d.deliverer_id) {
          this.delivererMode = 'existing';
          this.form.deliverer_id = d.deliverer_id;
        } else {
          this.delivererMode = 'new';
        }
        return;
      }

      // create
      this.form.delivery_fee = Number(this.defaultFee || 0);
      this.form.zone_choice = null;
      this.form.sale_id = null;
    },

    onZoneChanged() {
      if (this.form.zone_choice === 'other') {
        // Option A => default fee
        this.form.delivery_zone_id = null;
        this.form.delivery_fee = Number(this.defaultFee || 0);
        return;
      }

      const zoneId = this.form.zone_choice;
      this.form.delivery_zone_id = zoneId;

      const zone = (this.zones || []).find(z => z.id === zoneId);
      this.form.delivery_fee = zone ? Number(zone.fee || 0) : Number(this.defaultFee || 0);
      this.form.zone_name_custom = '';
    },

    sanitizePhoneField(event) {
      this.form.deliverer_phone = sanitizePhoneInput(event?.target?.value ?? this.form.deliverer_phone);
    },

    async submit() {
      if (this.isSubmitting) return;

      this.errorMessage = '';

      if (!this.form.sale_id) {
        this.errorMessage = 'Veuillez sélectionner une vente.';
        return;
      }

      if (!this.form.zone_choice) {
        this.errorMessage = 'Veuillez sélectionner une zone.';
        return;
      }

      if (this.form.zone_choice === 'other' && !(this.form.zone_name_custom || '').trim()) {
        this.errorMessage = 'Veuillez renseigner le nom de la zone (Autre).';
        return;
      }

      // deliverer validation
      if (this.delivererMode === 'existing') {
        // ok even if null (pending)
        this.form.deliverer_name = '';
        this.form.deliverer_phone = '';
      } else {
        this.form.deliverer_id = null;
        if (!(this.form.deliverer_name || '').trim()) {
          // autoriser livraison sans livreur? -> si tu veux oui, enlève ce bloc
          this.errorMessage = 'Veuillez saisir le nom du livreur (ou repassez en “Existant”).';
          return;
        }

        if (!isPhoneInputValid(this.form.deliverer_phone)) {
          this.errorMessage = phoneInputValidationMessage('téléphone du livreur');
          return;
        }
      }

      const payload = {
        sale_id: this.form.sale_id,
        delivery_zone_id: this.form.zone_choice === 'other' ? null : this.form.delivery_zone_id,
        zone_name_custom: this.form.zone_choice === 'other' ? (this.form.zone_name_custom || '').trim() : null,

        deliverer_id: this.delivererMode === 'existing' ? (this.form.deliverer_id || null) : null,
        deliverer_name: this.delivererMode === 'new' ? (this.form.deliverer_name || '').trim() : null,
        deliverer_phone: this.delivererMode === 'new' ? (sanitizePhoneInput(this.form.deliverer_phone).trim() || null) : null,

        notes: (this.form.notes || '').trim() || null,
      };

      this.isSubmitting = true;
      try {
        if (this.mode === 'edit' && this.delivery?.id) {
          // update simple (assigner livreur/statut/notes)
          await this.$api.put(`/deliveries/${this.delivery.id}`, {
            deliverer_id: payload.deliverer_id,
            notes: payload.notes,
          });
          this.$emit('saved', 'Livraison mise à jour.');
        } else {
          await this.$api.post('/deliveries', payload);
          this.$emit('saved', 'Livraison créée.');
        }
      } catch (e) {
        console.error('DeliveryForm submit', e);
        const errors = e.response?.data?.errors;
        this.errorMessage = errors ? Object.values(errors).flat().join(' ') : (e.response?.data?.message || 'Erreur enregistrement.');
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>
